/**
 * API functions for Project Team Members (Functional Roles)
 * 
 * These functions interact with the backend API to manage functional role assignments.
 * Functional roles describe job functions (Financial Planner, Engineer, etc.) and are
 * separate from permission-based project roles (Editor, Viewer).
 */

import { fetcher } from '../utils/fetcher';
import type {
  ProjectTeamMember,
  ProjectTeamMemberCreate,
  ProjectTeamMemberUpdate
} from '../types/team';

const TEAM_MEMBERS_URL = '/api/v1/team_members';

/**
 * Get all team member assignments, optionally filtered
 */
export const getTeamMembers = async (filters?: {
  project_id?: string;
  user_id?: string;
  functional_role?: string;
}): Promise<ProjectTeamMember[]> => {
  const params = new URLSearchParams();
  if (filters?.project_id) params.append('project_id', filters.project_id);
  if (filters?.user_id) params.append('user_id', filters.user_id);
  if (filters?.functional_role) params.append('functional_role', filters.functional_role);
  
  const url = params.toString() 
    ? `${TEAM_MEMBERS_URL}/read?${params.toString()}`
    : `${TEAM_MEMBERS_URL}/read`;
  
  return await fetcher(url);
};

/**
 * Get a specific team member assignment by ID
 */
export const getTeamMemberById = async (assignmentId: string): Promise<ProjectTeamMember> => {
  return await fetcher(`${TEAM_MEMBERS_URL}/read/${assignmentId}`);
};

/**
 * Create a new team member assignment
 */
export const createTeamMember = async (data: ProjectTeamMemberCreate): Promise<ProjectTeamMember> => {
  return await fetcher(`${TEAM_MEMBERS_URL}/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
};

/**
 * Update an existing team member assignment
 */
export const updateTeamMember = async (
  assignmentId: string,
  data: ProjectTeamMemberUpdate
): Promise<ProjectTeamMember> => {
  return await fetcher(`${TEAM_MEMBERS_URL}/update/${assignmentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
};

/**
 * Delete a team member assignment
 */
export const deleteTeamMember = async (assignmentId: string): Promise<boolean> => {
  return await fetcher(`${TEAM_MEMBERS_URL}/delete/${assignmentId}`, {
    method: 'DELETE'
  });
};
